if (!customElements.get('postcode-checker')) {
  const LEGACY_STORAGE_KEY = 'delivery-postcode';
  const MESSAGES = {
    required: 'Enter your postcode to check delivery for this product.',
    invalid: 'Please enter a valid postcode.',
    notFound: 'That postcode does not exist. Please check and try again.',
    lookupFailed: 'We could not verify that postcode. Please check and try again.',
    blocked: 'This product can’t be delivered to your postcode.',
    success: 'Great news — we deliver to your postcode.',
    confirm:
      'Are you sure you want to change the postcode? We will have to refresh your cart and remove all Frozen Items.',
    notAdded:
      'We can’t deliver Frozen Items to that postcode, so they have not been added to your cart.'
  };

  const toList = (value) =>
    (value || '')
      .split(/[\n,]/)
      .map((entry) => entry.trim())
      .filter(Boolean);

  const storageKey = () =>
    (window.FrozenPostcode && window.FrozenPostcode.STORAGE_KEY) || 'frozen_delivery_postcode';

  const normalise = (value) => {
    if (window.FrozenPostcode && typeof window.FrozenPostcode.normalizePostcode === 'function') {
      return window.FrozenPostcode.normalizePostcode(value);
    }
    return (value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  };

  const formatForDisplay = (value) => {
    if (window.FrozenPostcode && typeof window.FrozenPostcode.formatUkPostcode === 'function') {
      return window.FrozenPostcode.formatUkPostcode(value);
    }
    const clean = normalise(value);
    if (clean.length > 3) return `${clean.slice(0, -3)} ${clean.slice(-3)}`;
    return clean;
  };

  const readStoredPostcode = () => {
    const primary = localStorage.getItem(storageKey());
    if (primary) return primary;
    const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (legacy) {
      localStorage.setItem(storageKey(), normalise(legacy));
      return normalise(legacy);
    }
    return null;
  };

  const writeStoredPostcode = (postcode) => {
    const clean = normalise(postcode);
    localStorage.setItem(storageKey(), clean);
    // Keep legacy key in sync for older theme code / sessions.
    localStorage.setItem(LEGACY_STORAGE_KEY, clean);
    if (window.FrozenPostcode && typeof window.FrozenPostcode.syncCartPostcodeAttribute === 'function') {
      window.FrozenPostcode.syncCartPostcodeAttribute(clean);
    }
    // Lets the header modal, any other checker instance and the cart UI re-sync.
    document.dispatchEvent(new CustomEvent('on:postcode:updated', { detail: { postcode: clean } }));
  };

  class PostcodeChecker extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('.postcode-checker__input');
      this.message = this.querySelector('.postcode-checker__message');
      this.extras = this.dataset.blocked || '';
      // Merchant-editable in Theme settings -> Postcode delivery check.
      this.blockedMessage = this.dataset.blockedMessage || MESSAGES.blocked;
      this.matchBy = this.dataset.matchBy || 'type';
      this.matchValues = toList(this.dataset.matchValues).map((entry) => entry.toLowerCase());

      this.button = this.querySelector('.postcode-checker__button');
      this.button.addEventListener('click', () => this.check());
      this.input.addEventListener('keydown', (evt) => {
        if (evt.key === 'Enter') {
          evt.preventDefault();
          this.check();
        }
      });

      // variant-picker re-enables [name="add"] on load and on every variant change,
      // so re-assert the blocked state whenever something else flips it back on.
      const form = document.getElementById(this.dataset.formId);
      if (form) {
        new MutationObserver((mutations) => {
          if (this.deliverable !== false) return;
          mutations.forEach((mutation) => {
            if (mutation.target.disabled === false && this.buttons.indexOf(mutation.target) !== -1) {
              mutation.target.disabled = true;
            }
          });
        }).observe(form, { attributes: true, attributeFilter: ['disabled'], subtree: true });
      }

      this.syncHandler = () => this.syncFromStorage();
      document.addEventListener('on:postcode:updated', this.syncHandler);

      this.syncFromStorage();
    }

    disconnectedCallback() {
      document.removeEventListener('on:postcode:updated', this.syncHandler);
    }

    syncFromStorage() {
      const stored = readStoredPostcode();
      if (stored) {
        this.input.value = formatForDisplay(stored);
        this.apply(stored, false);
      } else {
        this.input.value = '';
        this.showSaved('');
        this.setState(false, MESSAGES.required, 'error');
      }
    }

    /**
     * Shows the saved postcode above the input and flips the button to "Change".
     */
    showSaved(postcode) {
      this.button.textContent = postcode ? 'Change' : 'Check';
      if (!this.saved) {
        this.saved = document.createElement('p');
        this.saved.className = 'postcode-checker__current';
        this.saved.style.margin = '0 0 8px';
        this.querySelector('.postcode-checker__form').before(this.saved);
      }
      this.saved.hidden = !postcode;
      this.saved.innerHTML = postcode
        ? `Current postcode: <strong>${formatForDisplay(postcode)}</strong>`
        : '';
    }

    get buttons() {
      const form = document.getElementById(this.dataset.formId);
      if (!form) return [];
      return Array.from(form.querySelectorAll('[name="add"], .shopify-payment-button button'));
    }

    setState(deliverable, text, tone) {
      this.deliverable = deliverable;
      this.buttons.forEach((button) => {
        button.disabled = !deliverable;
      });
      this.message.hidden = !text;
      this.message.textContent = text || '';
      this.message.className = `postcode-checker__message postcode-checker__message--${tone}`;
    }

    isBlocked(postcode) {
      if (window.FrozenPostcode && typeof window.FrozenPostcode.isPostcodeUndeliverableForFrozen === 'function') {
        return window.FrozenPostcode.isPostcodeUndeliverableForFrozen(postcode, this.extras);
      }
      // Fallback if matcher failed to load: treat theme extras as area/district prefixes.
      const clean = normalise(postcode);
      return toList(this.extras)
        .map(normalise)
        .some((prefix) => prefix && clean.startsWith(prefix));
    }

    isValidFormat(postcode) {
      if (window.FrozenPostcode && typeof window.FrozenPostcode.isValidUkPostcodeFormat === 'function') {
        return window.FrozenPostcode.isValidUkPostcodeFormat(postcode);
      }
      return /^[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2}$/.test(normalise(postcode));
    }

    /**
     * Applies a postcode: stores it and enables/disables the buy buttons.
     * Valid but undeliverable postcodes are still stored (location accepted, ATC blocked).
     */
    apply(postcode, store = true) {
      const clean = normalise(postcode);
      if (store) writeStoredPostcode(clean);
      this.input.value = formatForDisplay(clean);
      this.showSaved(clean);
      if (this.isBlocked(clean)) {
        this.setState(false, this.blockedMessage, 'error');
        return false;
      }
      this.setState(true, MESSAGES.success, 'success');
      return true;
    }

    async check() {
      if (this.button.getAttribute('aria-busy') === 'true') return;
      this.button.setAttribute('aria-busy', 'true');
      try {
        // Keeps the spinner visible long enough to read on local-only checks.
        await Promise.all([this.runCheck(), new Promise((resolve) => setTimeout(resolve, 450))]);
      } finally {
        this.button.removeAttribute('aria-busy');
      }
    }

    /**
     * Show an error without wiping a previously accepted postcode's ATC state.
     */
    showCheckError(text) {
      const previous = readStoredPostcode();
      this.deliverable = !!previous && !this.isBlocked(previous);
      if (previous && !this.isBlocked(previous)) {
        this.buttons.forEach((button) => {
          button.disabled = false;
        });
      } else {
        this.buttons.forEach((button) => {
          button.disabled = true;
        });
      }
      this.message.hidden = !text;
      this.message.textContent = text || '';
      this.message.className = 'postcode-checker__message postcode-checker__message--error';
    }

    async runCheck() {
      const clean = normalise(this.input.value);
      if (!clean) {
        this.showCheckError(MESSAGES.required);
        return;
      }
      if (!this.isValidFormat(clean)) {
        this.showCheckError(MESSAGES.invalid);
        return;
      }

      // Confirm the postcode exists before saving (blocks typos / made-up codes).
      if (window.FrozenPostcode && typeof window.FrozenPostcode.validatePostcodeExists === 'function') {
        const existence = await window.FrozenPostcode.validatePostcodeExists(clean);
        if (!existence.ok) {
          this.showCheckError(
            existence.reason === 'lookup_failed' ? MESSAGES.lookupFailed : MESSAGES.notFound
          );
          return;
        }
      }

      const previous = readStoredPostcode();
      const previousClean = previous ? normalise(previous) : null;

      // Any undeliverable postcode clears restricted items from the cart, not just a change.
      if (this.isBlocked(clean)) {
        const restrictedLines = await this.restrictedCartLines();
        if (restrictedLines.length) {
          // Changing an accepted postcode asks first; entering the first (undeliverable)
          // postcode just takes the items back out, so they never stay in the cart.
          if (previousClean) {
            // eslint-disable-next-line no-alert, no-restricted-globals
            if (!confirm(MESSAGES.confirm)) {
              this.input.value = formatForDisplay(previousClean);
              this.apply(previousClean, false);
              return;
            }
          } else {
            // eslint-disable-next-line no-alert
            alert(MESSAGES.notAdded);
          }
          writeStoredPostcode(clean);
          await this.removeCartLines(restrictedLines);
          window.location.reload();
          return;
        }
      }

      this.apply(clean);
    }

    /**
     * Returns the line item keys of cart items matching the configured types/tags.
     */
    async restrictedCartLines() {
      const response = await fetch(`${theme.routes.cart}.js`);
      const cart = await response.json();
      const keys = [];

      for (const item of cart.items) {
        // cart.js omits tags, so the product JSON is needed for tag matching.
        // eslint-disable-next-line no-await-in-loop
        const isRestricted = await this.itemMatchesRestriction(item);
        if (isRestricted) keys.push(item.key);
      }

      return keys;
    }

    async itemMatchesRestriction(item) {
      const needles = this.matchValues;
      if (!needles.length) return false;

      const type = (item.product_type || '').toLowerCase();
      const typeHit = needles.some((needle) => type.includes(needle));

      if (this.matchBy === 'type') return typeHit;
      if (this.matchBy === 'both' && typeHit) return true;

      if (this.matchBy === 'tag' || this.matchBy === 'both') {
        const tags = await this.productTags(item.handle);
        return tags.some((tag) => needles.some((needle) => tag.includes(needle)));
      }

      return false;
    }

    async productTags(handle) {
      this.tagCache = this.tagCache || {};
      if (!this.tagCache[handle]) {
        try {
          const response = await fetch(`/products/${handle}.js`);
          const product = await response.json();
          this.tagCache[handle] = (product.tags || []).map((tag) => tag.toLowerCase());
        } catch (error) {
          this.tagCache[handle] = [];
        }
      }
      return this.tagCache[handle];
    }

    /**
     * Removes lines by line item key. /cart/change.js is used (one request per line,
     * cart writes can't run in parallel) because it reliably accepts line item keys.
     */
    async removeCartLines(keys) {
      for (const key of keys) {
        // eslint-disable-next-line no-await-in-loop
        const response = await fetch(`${theme.routes.cart}/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 })
        });
        if (!response.ok) {
          console.log(`postcode-checker: could not remove line ${key}`); // eslint-disable-line no-console
        }
      }
    }
  }

  customElements.define('postcode-checker', PostcodeChecker);
}
